import { LOCALE_ID, NgModule } from '@angular/core';
import { IntlModule, localeData, Locale } from "@progress/kendo-angular-intl";
// Load all required data for the es locale
import "@progress/kendo-angular-intl/locales/es/all";

import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { GridModule, PDFModule, ExcelModule } from '@progress/kendo-angular-grid';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { MenusModule } from '@progress/kendo-angular-menu';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { UploadsModule } from '@progress/kendo-angular-upload';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { ContextMenuModule } from '@progress/kendo-angular-menu';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ChartsModule } from '@progress/kendo-angular-charts';
import 'hammerjs';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { TooltipModule } from '@progress/kendo-angular-tooltip';

import { TrimDirective } from '@app/core/directives';
import { JwtInterceptor, ErrorInterceptor, UploadInterceptor } from '@app/core/interceptors';
import { AlertService, DatasourceService } from '@app/core/services';
import { 
  VehiclesReportComponent, 
  ServicesReportComponent,
  ExpensesReportComponent, 
  DiarioReportComponent,
  LiberacionReportComponent
} from '@app/core/reports';
import {  
  KendoGridCellColorComponent, 
  KendoGridCellDateComponent, 
  KendoGridCellIconComponent,
  KendoGridCellImageComponent
} from '@app/core/components/renders';
import { ImagesScrollviewComponent } from '@app/core/components/scrollviews';
import { DynamicAutocompleteComponent } from '@app/core/components/autocompletes';
import { DynamicDropdownlistComponent } from '@app/core/components/dropdownlists';
import { 
  VehiclesListComponent,
  ServicesListComponent,
  ServicesListByStatusComponent,
  ExpensesListComponent,
  CreditsListComponent
} from '@app/core/components/lists';
import { CatalogMultiselectComponent } from '@app/core/components/multiselects';
import { 
  BaseContextmenuComponent, 
  RoleContextmenuComponent,
  VehicleContextmenuComponent,  
  ServiceContextmenuComponent,
  CreditContextmenuComponent,
  ReceiptContextmenuComponent
} from '@app/core/components/contextmenus';
import { 

  CreateEditWindowComponent,
  CreateEditActionComponent,
  CreateEditRoleComponent,
  CreateEditUserComponent,
  CreateEditPermissionComponent,
  CreateEditAuthorityComponent,
  CreateEditPersonComponent,
  CreateEditVehicleTypeComponent,
  CreateEditVehicleStatusComponent,
  CreateEditDepositComponent,
  CreateEditVehicleEntryComponent,
  CreateEditCraneComponent,
  CreateEditPersonOperatorComponent,
  CreateEditServiceOutputComponent,
  EditVehicleInventoryComponent,
  EditServiceInventoryComponent,
  EditVehicleInventoryCostCraneComponent,
  CreateEditPaymentTypeComponent,
  CreateEditServiceStatusComponent,
  CreateEditExpenseTypeComponent,
  CreateEditExpenseComponent,
  CreateEditCustomerComponent,
  CreateEditPaymentComponent,
  EditInvoiceComponent,

  EditUserPasswordComponent, 
  EditRolePermissionsComponent,

  CancelWindowComponent,
  CancelReceiptComponent,

  ConfigWindowComponent,
  ConfigDateComponent,
  ConfigRangeDateComponent,

  DetailVehicleImagesComponent,
  DetailCreditPaymentsComponent,
  DetailReceiptComponent

} from '@app/core/windows';

import { ImagenUploadComponent, MultipleImagenUploadComponent } from '@app/core/components/uploads' 

import { LoginComponent } from '@app/login';

import { 

  BaseCrudComponent,  
  VersionsCrudComponent,
  ActionsCrudComponent,
  RolesCrudComponent,
  UsersCrudComponent,
  PermissionsCrudComponent,
  AuthoritiesCrudComponent,
  PersonsCrudComponent,
  VehiclesTypesCrudComponent,  
  VehiclesStatusCrudComponent,
  DepositsCrudComponent,
  VehiclesCrudComponent,
  CranesCrudComponent,
  PersonsOperatorsCrudComponent,
  ServicesCrudComponent,
  PaymentsTypesCrudComponent,
  ServicesStatusCrudComponent,
  ExpensesTypesCrudComponent,
  ExpensesCrudComponent,
  CustomersCrudComponent,
  CreditsCrudComponent,
  AccountsCrudComponent,
  ReceiptsCrudComponent,

  WelcomeComponent,
  ProfileComponent

} from '@app/admin';
import { ScrollViewModule } from '@progress/kendo-angular-scrollview';

import { environment } from '@environments/environment';
import "@progress/kendo-date-math/tz/America/Mexico_City";

const esData: Locale = localeData(environment.locale);

esData.numbers.symbols.decimal = ".";
esData.numbers.symbols.group = ",";
esData.calendar.days.format.short = [
  "Do",
  "Lu",
  "Ma",
  "Mi",
  "Ju",
  "Vi",
  "Sa"
];
esData.calendar.months.format.abbreviated = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic"
];
esData.calendar.months.format.wide = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre"
]

// import { SocketIoModule, SocketIoConfig } from 'ngx-socket-io';

// const config: SocketIoConfig = { 
//   url: environment.wsUrl, 
//   options: {} 
// };

@NgModule({
  declarations: [
    AppComponent,
    
    LoginComponent,

    BaseCrudComponent,
    VersionsCrudComponent,
    ActionsCrudComponent,
    RolesCrudComponent,
    UsersCrudComponent,
    PermissionsCrudComponent,
    AuthoritiesCrudComponent,
    PersonsCrudComponent,
    VehiclesTypesCrudComponent,
    VehiclesStatusCrudComponent,
    DepositsCrudComponent,
    VehiclesCrudComponent,
    CranesCrudComponent,
    PersonsOperatorsCrudComponent,
    ServicesCrudComponent,
    PaymentsTypesCrudComponent,
    ServicesStatusCrudComponent,
    ExpensesTypesCrudComponent,
    ExpensesCrudComponent,
    CustomersCrudComponent,
    CreditsCrudComponent,
    AccountsCrudComponent,
    ReceiptsCrudComponent,
    
    DynamicAutocompleteComponent,
    
    DynamicDropdownlistComponent,

    VehiclesListComponent,
    ServicesListComponent,
    ServicesListByStatusComponent,
    ExpensesListComponent,
    CreditsListComponent,

    KendoGridCellColorComponent,
    KendoGridCellDateComponent,
    KendoGridCellIconComponent,
    KendoGridCellImageComponent,

    ImagesScrollviewComponent,

    CatalogMultiselectComponent,

    BaseContextmenuComponent,
    RoleContextmenuComponent,
    VehicleContextmenuComponent,    
    ServiceContextmenuComponent,
    CreditContextmenuComponent,
    ReceiptContextmenuComponent,

    CreateEditWindowComponent,

    CreateEditActionComponent,
    CreateEditRoleComponent,
    CreateEditUserComponent,
    CreateEditPermissionComponent,
    CreateEditAuthorityComponent,
    CreateEditPersonComponent,
    CreateEditVehicleTypeComponent,
    CreateEditVehicleStatusComponent,
    CreateEditDepositComponent,
    CreateEditVehicleEntryComponent,
    CreateEditCraneComponent,
    CreateEditPersonOperatorComponent,
    CreateEditServiceOutputComponent,
    EditVehicleInventoryComponent,
    EditServiceInventoryComponent,
    EditVehicleInventoryCostCraneComponent,
    CreateEditPaymentTypeComponent,
    CreateEditServiceStatusComponent,
    CreateEditExpenseTypeComponent,
    CreateEditExpenseComponent,
    CreateEditCustomerComponent,
    CreateEditPaymentComponent,
    EditInvoiceComponent,

    EditUserPasswordComponent, 
    EditRolePermissionsComponent,

    CancelWindowComponent,
    CancelReceiptComponent,

    ConfigWindowComponent,
    ConfigDateComponent,
    ConfigRangeDateComponent,

    DetailVehicleImagesComponent,
    DetailCreditPaymentsComponent,
    DetailReceiptComponent,

    ImagenUploadComponent,
    MultipleImagenUploadComponent,

    WelcomeComponent,
    ProfileComponent,

    TrimDirective,

    VehiclesReportComponent,
    ServicesReportComponent,
    ExpensesReportComponent,
    DiarioReportComponent,   
    LiberacionReportComponent 

  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    HttpClientModule,
    IntlModule,
    GridModule, 
    PDFModule, 
    ExcelModule,
    BrowserAnimationsModule,
    DialogsModule,
    InputsModule,
    ExcelExportModule,
    PDFExportModule,
    DropDownsModule,
    UploadsModule,
    // GridContextMenuComponent,
    // MenuModule,
    MenusModule,
    ContextMenuModule,
    LayoutModule,
    ChartsModule,
    ProgressBarModule,
    ButtonsModule,
    NavigationModule,
    DateInputsModule,
    LabelModule,
    TooltipModule,
    ScrollViewModule,
    // SocketIoModule.forRoot(config)    
  ],
  providers: [
    { provide: LOCALE_ID, useValue: environment.locale},
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UploadInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: MultipleImagenUploadComponent, multi: true },
    AlertService,
    DatasourceService,
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
